import React, { useState, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { MessageCircle, X, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FeedbackButton = () => {
  const [showDialog, setShowDialog] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Draggable state for mobile
  const [pos, setPos] = useState({ x: null, y: null });
  const dragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const moved = useRef(false);
  const btnRef = useRef(null);

  const onTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    const rect = btnRef.current.getBoundingClientRect();
    dragOffset.current = { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
    dragging.current = true;
    moved.current = false;
  }, []);

  const onTouchMove = useCallback((e) => {
    if (!dragging.current) return;
    moved.current = true;
    const touch = e.touches[0];
    const x = Math.max(0, Math.min(window.innerWidth - 40, touch.clientX - dragOffset.current.x));
    const y = Math.max(0, Math.min(window.innerHeight - 40, touch.clientY - dragOffset.current.y));
    setPos({ x, y });
    e.preventDefault();
  }, []);

  const onTouchEnd = useCallback(() => {
    dragging.current = false;
    if (!moved.current) {
      setShowDialog(true);
    }
  }, []);

  const handleSubmit = async () => {
    if (!feedback.trim()) { toast.error('請輸入反饋內容'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/feedback/submit`, { content: feedback, type: 'general' });
      toast.success('感謝您的反饋！');
      setFeedback('');
      setShowDialog(false);
    } catch { toast.error('提交失敗，請稍後再試'); }
    finally { setSubmitting(false); }
  };

  const mobileStyle = pos.x !== null
    ? { left: pos.x, top: pos.y, right: 'auto', bottom: 'auto' }
    : { right: 16, top: 12 };

  return (
    <>
      {/* Desktop: fixed bottom-right */}
      <button
        onClick={() => setShowDialog(true)}
        className="fixed bottom-5 right-5 z-40 w-10 h-10 bg-obsidian-800 hover:bg-obsidian-700 border border-obsidian-600 hover:border-lime-500/40 text-obsidian-100 hover:text-lime-500 rounded-full transition-all duration-200 items-center justify-center hidden md:flex"
        aria-label="反饋"
        data-testid="feedback-btn"
      >
        <MessageCircle className="w-4 h-4" strokeWidth={2} />
      </button>

      {/* Mobile: draggable feedback button */}
      <button
        ref={btnRef}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={() => { if (!moved.current) setShowDialog(true); }}
        className="fixed z-40 w-9 h-9 bg-obsidian-800/80 active:bg-obsidian-700 border border-obsidian-600 text-obsidian-100 rounded-full shadow-lg flex items-center justify-center md:hidden touch-none"
        style={mobileStyle}
        aria-label="反饋"
        data-testid="feedback-btn-mobile"
      >
        <MessageCircle className="w-3.5 h-3.5" strokeWidth={2} />
      </button>

      {showDialog && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50" onClick={() => setShowDialog(false)}>
          <div className="surface-glass-dark p-6 max-w-md w-full mx-4 rounded-2xl shadow-obsidian" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-lime-500" strokeWidth={2} />
                <h3 className="font-display text-base font-semibold text-white tracking-tight">用戶反饋</h3>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowDialog(false)} className="h-7 w-7 p-0 rounded-md text-obsidian-300 hover:text-white hover:bg-white/5">
                <X className="w-4 h-4" />
              </Button>
            </div>
            <p className="label-micro text-obsidian-300 mb-4">您的意見對我們非常重要</p>
            <Textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="請輸入您的反饋內容…"
              className="min-h-[120px] mb-4 rounded-xl bg-obsidian-900 border-obsidian-600 focus:border-lime-500/50 text-white placeholder:text-obsidian-400 text-sm"
              data-testid="feedback-textarea"
              style={{ fontSize: '16px' }}
            />
            <div className="flex gap-2">
              <Button onClick={handleSubmit} disabled={submitting || !feedback.trim()}
                className="flex-1 rounded-xl btn-neon text-sm font-semibold disabled:opacity-40" data-testid="feedback-submit-btn">
                <Send className="w-3.5 h-3.5 mr-2" />{submitting ? '提交中…' : '提交反饋'}
              </Button>
              <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-xl border-obsidian-600 bg-transparent text-obsidian-100 hover:bg-white/5 text-sm">取消</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackButton;
